<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { RouterView } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import { useChatStore } from '@/stores/chat.store';
import ToastHost from '@/components/ui/ToastHost.vue';

const auth = useAuthStore();
const notifications = useNotificationsStore();
const chat = useChatStore();

onMounted(async () => {
  await auth.bootstrap();
});

// Start listening for live notifications as soon as somebody is signed in.
watch(
  () => auth.isAuthenticated,
  async (authenticated) => {
    if (!authenticated) return;
    notifications.listen();
    // Chat listens from sign-in rather than from opening a panel: an unread
    // badge that only moved while you were already reading would be pointless.
    chat.listen();
    // The inbox count is best-effort: a badge is never worth a failed sign-in.
    await Promise.all([
      notifications.refreshUnread(),
      notifications.loadChannels(),
      notifications.refreshInbox().catch(() => undefined),
      chat.refreshSummary().catch(() => undefined),
    ]);

    // A browser stops here; the Tauri app asks the OS and registers its FCM token.
    if (await notifications.enableMobilePush().catch(() => false)) {
      const channels = auth.user?.notificationPreferences.channels;
      if (channels) channels.mobile_push = true;
    }
  },
  { immediate: true },
);
</script>

<template>
  <RouterView />
  <ToastHost />
</template>
